import { Plus } from 'lucide-react';
import { useEffect, useState } from 'react';

import { AutocompleteInput } from '../../../components/AutocompleteInput';
import { Button } from '../../../components/Button';
import { useSuggestLabelsQuery } from '../tickets.api';

type Props = {
    existing: string[];
    onAdd: (label: string) => void;
    disabled?: boolean;
};

// Free-text label entry with inline autocomplete: as you type, the best match
// among labels already used across tickets completes inline (Tab / ArrowRight
// accepts it). Enter adds whatever is typed, so brand-new labels still work.
// Already-applied labels (case-insensitive) are excluded from the suggestions.
export function LabelInput({ existing, onAdd, disabled }: Props) {
    const [input, setInput] = useState('');
    const [query, setQuery] = useState('');

    // Debounce the suggestion lookup so each keystroke doesn't hit the API.
    useEffect(() => {
        const t = setTimeout(() => setQuery(input.trim()), 200);
        return () => clearTimeout(t);
    }, [input]);

    const { data: raw } = useSuggestLabelsQuery(query, { skip: query.length === 0 });
    const existingSet = new Set(existing.map((e) => e.toLowerCase()));
    const suggestions = (raw ?? []).filter((s) => !existingSet.has(s.toLowerCase()));

    const add = (value: string) => {
        const v = value.trim();
        setInput('');
        if (!v || existingSet.has(v.toLowerCase())) return;
        onAdd(v);
    };

    return (
        <div className="flex gap-2">
            <AutocompleteInput
                value={input}
                onChange={setInput}
                suggestions={suggestions}
                onCommit={add}
                placeholder="Adauga eticheta..."
                aria-label="Adauga eticheta"
                disabled={disabled}
            />
            <Button
                type="button"
                variant="secondary"
                disabled={disabled || !input.trim()}
                onClick={() => add(input)}
                aria-label="Adauga eticheta"
                className="shrink-0"
            >
                <Plus aria-hidden="true" className="h-4 w-4" />
            </Button>
        </div>
    );
}
